import { useMemo } from 'react';

// The single source of truth for "what counts as a finding" across the
// whole app — originally lived only inside ArbejdskoPage.jsx, which meant
// Oversigt.jsx's own "Start Her" summary re-derived its own, simpler count
// from the same raw data and silently drifted from Arbejdskø's real number
// (missing buckets like inactive accounts, missing the personType/
// accountEnabled filters on MFA, etc.). Every caller that needs "how many
// real findings are there" should go through this hook instead of
// recomputing it, so the two pages can never show two different numbers
// for the same tenant again.

export function daysSince(iso) {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
}

// "I dag" / "3 dage siden" — the one wording every finding's "last seen"-style row uses.
function ago(days) {
  if (days == null) return 'Ikke tilgængeligt endnu';
  if (days === 0) return 'I dag';
  return days === 1 ? '1 dag siden' : `${days} dage siden`;
}

function fmtBytes(bytes) {
  if (bytes == null || Number.isNaN(bytes)) return 'Ikke tilgængeligt endnu';
  const gb = bytes / 1024 ** 3;
  return gb >= 1 ? `${gb.toFixed(1).replace('.', ',')} GB` : `${Math.round(bytes / 1024 ** 2)} MB`;
}

// A short "Anna K., Michael J. og 2 til" style line for a role's holders.
function nameList(names) {
  const unique = [...new Set(names.filter(Boolean))];
  if (unique.length <= 2) return unique.join(' og ');
  return `${unique.slice(0, 2).join(', ')} og ${unique.length - 2} til`;
}

// How many devices have "no reported security status" for real — the same population as this
// hook's own 'enheder-agent' bucket below (an unknown threat state, on a device that isn't ALREADY
// a finding for a real compliance reason), just exposed as a plain number so Konfiguration's
// "Enheder uden rapporteret sikkerhedsstatus" setting can share the exact count instead of reading
// the backend's coarser noThreatAgentCount field (every noncompliant device counts as "having a
// reason" there, even an unclassified one — see the enheder-agent bucket — so this reduces to
// exactly "unknown threat state AND otherwise compliant").
export function noReportedStatusCount(devicesRaw) {
  if (!Array.isArray(devicesRaw)) return null;
  return devicesRaw.filter((d) => (d.partnerReportedThreatState || 'unknown') === 'unknown' && d.complianceState === 'compliant').length;
}

// ---- Enheder — same classifyReason/REASON_* logic as EnhederPage.jsx ----
function classifyReason(reason) {
  const r = (reason || '').toLowerCase();
  if (r.includes('bitlocker') || r.includes('encrypt')) return 'Mangler kryptering';
  if (r.includes('defender') || r.includes('mde') || r.includes('antivirus') || r.includes('malware')) return 'Defender ikke onboardet';
  if (/(^|[^a-z])os/.test(r) || r.includes('version') || r.includes('build')) return 'For gammel OS-version';
  return 'Fejlet compliance-politik';
}
// What Intune reports about a device's own policies, in words, and which system it runs: so a row says what fails, on what.
const FAIL_STATES = ['nonCompliant', 'error', 'conflict'];
const FAIL_STATE_TEXT = { nonCompliant: 'ikke opfyldt', error: 'fejl', conflict: 'konflikt' };

// ---- One builder per kind of object ----
// What an object is, as facts, is built here once and used by the findings and by "I orden" alike, so the same app, account or
// device shows the same facts whichever list it stands in.
const WRITE_ALL_RE = /(\.ReadWrite\.All|\.Write\.All|FullControl\.All|\.Send$)/i;
export const appHasWrite = (a) => (a.permissions || []).some((p) => WRITE_ALL_RE.test(p.name || ''));
export const prepApp = (a) => ({ ...a, daysSinceUsed: daysSince(a.lastSignInDateTime), ageDays: daysSince(a.createdDateTime) });
// "Ingen registreret" only when Microsoft really answered; when it did not, we do not know, and say so.
const appUsedText = (a) => (a.lastSignInAvailable !== true ? 'Ikke tilgængeligt endnu' : a.daysSinceUsed == null ? 'Ingen registreret' : a.daysSinceUsed === 0 ? 'I dag' : `${a.daysSinceUsed} dage siden`);
// Who publishes it (unknown is a fact, not a finding), what it may do, when it was last used and how old it is.
export const appItemOf = (a) => ({
  id: a.id, name: a.displayName || 'N/A', sub: a.publisherName || '', navPath: `applikationer/${a.id}`,
  meta: [
    ['Tilladelser', `${a.permissionCount ?? 0}`],
    ['Skrivetilladelser', appHasWrite(a) ? 'Ja' : 'Nej'],
    ['Sidst brugt', appUsedText(a)],
    ['Microsoft-data om brug', a.lastSignInAvailable === true ? 'Tilgængelig' : 'Ikke tilgængelig'],
    ['Udgiver', a.publisherName || 'Ukendt udgiver'],
    ['Verificeret udgiver', a.hasVerifiedPublisher ? 'Ja' : 'Nej'],
    ...(a.ownerOrganizationId ? [['Ejer-tenant', a.ownerOrganizationId]] : []),
    ['Oprettet', a.ageDays == null ? 'Ikke tilgængeligt endnu' : `${a.ageDays} dage siden`],
  ],
});

// A member account with what the sign-in report says about it (the same merge for every list).
export function mergeMembers(mfa, risks) {
  const signInByUserId = {};
  (risks?.memberSignIns || []).forEach((u) => { signInByUserId[u.id] = u; });
  return (mfa?.users || []).map((u) => {
    const signIn = signInByUserId[u.id];
    return {
      ...u,
      personType: 'member',
      daysSinceSignIn: signIn ? signIn.daysSinceSignIn : undefined,
      passwordAgeDays: signIn ? daysSince(signIn.lastPasswordChangeDateTime) : null,
      onPremisesSyncEnabled: signIn ? signIn.onPremisesSyncEnabled : undefined,
      licenseCount: signIn ? signIn.licenseCount : undefined,
    };
  });
}
const MFA_METHOD_LABEL = {
  microsoftAuthenticatorPush: 'Authenticator-app', microsoftAuthenticator: 'Authenticator-app', softwareOneTimePasscode: 'Kode-app (TOTP)', hardwareOneTimePasscode: 'Hardware-kode',
  fido2SecurityKey: 'Sikkerhedsnøgle', passKeyDeviceBound: 'Passkey', windowsHelloForBusiness: 'Windows Hello', temporaryAccessPass: 'Midlertidig adgangskode',
  mobilePhone: 'SMS/opkald', alternateMobilePhone: 'SMS/opkald', officePhone: 'Telefonopkald', email: 'E-mail',
};
export const personItemOf = (u) => ({
  id: u.id, name: u.displayName || 'Ukendt', sub: u.personType === 'guest' ? 'Gæst' : 'Bruger i organisationen', navPath: `identitet/${u.id}`,
  meta: u.personType === 'guest'
    ? [['Seneste login', u.daysSinceSignIn == null ? 'Ikke tilgængeligt endnu' : u.daysSinceSignIn === 0 ? 'I dag' : `${u.daysSinceSignIn} dage siden`]]
    : [
        ['Seneste login', u.daysSinceSignIn == null ? 'Ikke tilgængeligt endnu' : u.daysSinceSignIn === 0 ? 'I dag' : `${u.daysSinceSignIn} dage siden`],
        ['Ekstra loginbeskyttelse', u.hasMfa ? 'Aktiveret' : 'Mangler'],
        // Shown only when there is something to show (no "none" line for an account without MFA: that is already said above).
        ...(Array.isArray(u.methods) && u.methods.length > 0 ? [['MFA-metoder', [...new Set(u.methods.map((m) => MFA_METHOD_LABEL[m] || m))].join(', ')]] : []),
        ['Adgangskode skiftet', u.passwordAgeDays == null ? 'Ikke tilgængeligt endnu' : u.passwordAgeDays + ' dage siden'],
        ['Kontostatus', u.accountEnabled === false ? 'Deaktiveret' : 'Aktiv'],
      ],
});

// The rows every device shows: system, readiness for encryption, whether it is encrypted, and the last sync.
export const deviceOsRow = (d) => ['Styresystem', d.operatingSystem || 'Ikke tilgængeligt endnu'];
export const deviceReadyRows = (d) => (d.encryptionReadiness !== undefined
  ? [['Klar til kryptering', d.encryptionReadiness === 'ready' ? 'Ja' : d.encryptionReadiness === 'notReady' ? 'Nej' : 'Ikke tilgængeligt endnu'], ['TPM-version', d.tpmVersion ? (/^unknown$/i.test(d.tpmVersion) ? 'Ukendt' : d.tpmVersion) : 'Ikke tilgængeligt endnu']]
  : []);
export const deviceEncRow = (d) => ['Krypteret', d.isEncrypted === null || d.isEncrypted === undefined ? 'Ikke tilgængeligt endnu' : d.isEncrypted ? 'Ja' : 'Nej'];
export const deviceSyncRow = (d) => { const days = daysSince(d.lastSyncDateTime); return ['Sidst synkroniseret', days == null ? 'Ikke tilgængeligt endnu' : days === 0 ? 'I dag' : `${days} dage siden`]; };

// What a mailbox uses of its space.
export const mailboxUsageRows = (m) => [
  ['Brugt', `${fmtBytes(m.storageUsedBytes)} af ${fmtBytes(m.quotaBytes)}`],
  ['Fyldt', `${Math.round((m.storageUsedBytes / m.quotaBytes) * 100)} %`],
];

// What a domain's public DNS says, as the rows of a domain finding: SPF, DKIM and DMARC. A value that was not read is never guessed.
export function dkimText(d) {
  if (!d?.dkim) return 'Ikke tilgængeligt endnu';
  if (d.dkim.records.length === 0) return 'Ingen record fundet';
  return d.dkim.microsoft ? 'Konfigureret, Microsoft 365-nøgler' : 'Konfigureret';
}
export const domainMeta = (d) => [
  ['SPF', d.spf?.configured ? 'Konfigureret' : 'Mangler'],
  ['DKIM', dkimText(d)],
  ['DMARC', d.dmarc?.configured ? `Politik: ${d.dmarc.policy || 'ukendt'}` : 'Mangler'],
];

// Why something is in order, in words — what the side panel says for a row under "I orden". One line per check.
const OK_WHY = {
  'ok-id-mfa': 'Kontoen har totrinsgodkendelse (MFA) sat op, så et stjålet kodeord alene ikke giver adgang.',
  'ok-roller-mfa': 'Administratoren har MFA sat op, så en administratorkonto ikke kan overtages med kodeordet alene.',
  'ok-enheder': 'Enheden lever op til alle jeres compliance-regler i Intune.',
  'ok-enheder-krypteret': 'Enhedens disk er krypteret, så data ikke kan læses, hvis enheden bliver væk.',
  'ok-exchange-forward': 'Postkassen sender ikke mails videre til adresser uden for organisationen.',
  'ok-exchange-domain': 'Domænet har SPF og DMARC sat op, og DMARC håndhæver, så andre ikke let kan udgive sig for at være jer.',
  'ok-ca-enabled': 'Reglen er slået til og håndhæver.',
};
// A policy can be listed once for the System account and once for the signed-in person: it is one policy, named once.
// Every policy (compliance or configuration) that fails on the device, each named once.
const failingAll = (d) => {
  const seen = new Set();
  if (d.statesFromEarlierRead) return [];
  return [...(d.compliancePolicyStates || []), ...(d.configurationStates || [])].filter((s) => FAIL_STATES.includes(s.state) && s.displayName && !seen.has(s.displayName) && seen.add(s.displayName));
};
const failingPolicies = (d) => {
  const seen = new Set();
  if (d.statesFromEarlierRead) return [];
  return (d.compliancePolicyStates || []).filter((s) => FAIL_STATES.includes(s.state) && !seen.has(s.displayName) && seen.add(s.displayName));
};
export function platformOf(os) {
  const o = String(os || '').toLowerCase();
  if (o.includes('windows')) return 'windows';
  if (o.includes('mac')) return 'macos';
  if (o.includes('ios') || o.includes('ipad')) return 'ios';
  if (o.includes('android')) return 'android';
  return null;
}
const PLATFORM_LABEL = { windows: 'Windows', macos: 'Mac', ios: 'iOS', android: 'Android' };
// The names Intune uses for the settings of a compliance policy (as its own "Setting compliance" report lists them), in Danish.
const SETTING_TEXT = {
  bitlocker: 'BitLocker', 'encryption of data storage on device': 'Kryptering af disken', antivirus: 'Antivirus', 'anti-spyware': 'Anti-spyware',
  'real-time protection': 'Realtidsbeskyttelse', firewall: 'Firewall', 'secure boot': 'Secure Boot', 'code integrity': 'Code Integrity',
  'trusted platform module (tpm)': 'TPM', 'microsoft defender antimalware': 'Defender Antimalware',
  'require the device to be at or under the machine risk score': 'Enhedens risikoscore', 'has a compliance policy assigned': 'Ingen compliance-politik tildelt',
};
// Graph spells a setting as 'Windows10CompliancePolicy.AntivirusRequired'; the report as 'Antivirus'. Both end up as one short Danish name.
const SETTING_BY_KEY = {
  antivirusrequired: 'Antivirus', antispywarerequired: 'Anti-spyware', activefirewallrequired: 'Firewall', firewallrequired: 'Firewall',
  storagerequireencryption: 'Kryptering af disken', bitlockerenabled: 'BitLocker', securebootenabled: 'Secure Boot', codeintegrityenabled: 'Code Integrity',
  tpmrequired: 'TPM', defenderenabled: 'Defender', rtpenabled: 'Realtidsbeskyttelse', passwordrequired: 'Adgangskode', osminimumversion: 'Mindste OS-version',
  osmaximumversion: 'Højeste OS-version', devicethreatprotectionrequiredsecuritylevel: 'Trusselsniveau', signaturesoutofdate: 'Defender-signaturer', defenderversion: 'Defender-version',
  storagerequiredeviceencryption: 'Kryptering af disken', secureboot: 'Secure Boot', codeintegrity: 'Code Integrity', firewall: 'Firewall',
};
const humanize = (s) => { const w = String(s).replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').trim(); return w.charAt(0).toUpperCase() + w.slice(1); };
export const settingText = (raw) => {
  const key = String(raw || '').toLowerCase().trim();
  if (SETTING_TEXT[key]) return SETTING_TEXT[key];
  const last = String(raw || '').split('.').pop();
  return SETTING_BY_KEY[last.toLowerCase()] || (last.includes(' ') ? last : humanize(last));
};
const NOT_ENCRYPTED_TEXT = { windows: 'BitLocker er ikke slået til', macos: 'FileVault er ikke slået til' };
const nameList2 = (names) => (names.length <= 2 ? names.join(', ') : `${names.slice(0, 2).join(', ')} og ${names.length - 2} til`);
const REASON_ORDER = ['Mangler kryptering', 'For gammel OS-version', 'Defender ikke onboardet', 'Fejlet compliance-politik', 'Compliance-afvigelse'];
const REASON_TONE = { 'Mangler kryptering': 'bad', 'For gammel OS-version': 'bad', 'Defender ikke onboardet': 'warn', 'Fejlet compliance-politik': 'warn', 'Compliance-afvigelse': 'warn' };
// hint = the short line under the title, about = what it means in plain
// words (only what Microsoft actually reports), recommend = what to do —
// left out where there's nothing to do, only something to know.
const REASON_HINT = {
  'Mangler kryptering': 'Disken er ikke krypteret',
  'For gammel OS-version': 'Styresystemet lever ikke op til kravet',
  'Defender ikke onboardet': 'Sender ikke status til Defender',
  'Fejlet compliance-politik': 'Overholder ikke en regel i Intune',
  'Compliance-afvigelse': 'Markeret som ikke i orden uden oplyst årsag',
};
const REASON_ABOUT = {
  'Mangler kryptering': 'Microsoft viser, at enhedens disk ikke er krypteret (BitLocker på Windows, FileVault på Mac). Uden kryptering kan data på en tabt eller stjålet enhed være læsbare.',
  'For gammel OS-version': 'Enhedens styresystem lever ikke op til jeres krav i Intune.',
  'Defender ikke onboardet': 'Enheden sender ikke status til Microsoft Defender (virusbeskyttelsen).',
  'Fejlet compliance-politik': 'Enheden opfylder ikke en af jeres regler i Intune.',
  'Compliance-afvigelse': 'Intune har markeret enheden som ikke i orden, men har ikke oplyst hvorfor.',
};
const REASON_RECOMMEND = {
  'Mangler kryptering': 'Slå BitLocker (Windows) eller FileVault (Mac) til på enheden. Er hardwaren ikke klar, må enheden udskiftes.',
  'For gammel OS-version': 'Opdater styresystemet på enheden til den version, jeres regel kræver.',
  'Defender ikke onboardet': 'Sørg for, at antivirus og Defender kører og er opdateret på enheden.',
  'Fejlet compliance-politik': 'Ret det, politikken kræver, på enheden, og lad den synkronisere igen.',
  'Compliance-afvigelse': 'Intune oplyser ikke hvorfor. Lad enheden synkronisere igen, og tjek dens politikker.',
};
// ---- One object, one finding ----
// The same account, app, device or workspace is never counted under several headings. Findings about the same kind of thing are
// merged into one (below), and an object that is still in several keeps only the most serious one, with the other headings as a
// line on it (Evidens: "Også fundet"). Decisions written under an old key still count: see canonicalFindingKey.
const MERGES = [
  { into: 'app-big', rank: -1, tone: 'bad', area: 'applications', title: 'Apps med stor adgang', hint: 'Kan ændre i stor skala, eller har mange tilladelser',
    about: 'Appen har tilladelser, der kan ændre eller sende data for alle (fx skrive til alle filer eller sende mail), eller 10 eller flere tilladelser. Bruges de forkert, er skaden stor.',
    recommend: "Kender I ikke appen, eller skal den ikke have så bred adgang, så deaktivér eller slet den. Ellers fjern de tilladelser, den ikke har brug for.",
    from: [['app-write-all', 'skriveadgang til hele tenanten'], ['app-priv', 'mange tilladelser']] },
  { into: 'enheder-afviger', tone: 'warn', area: 'devices', title: 'Enheder, der afviger', hint: 'Afviger fra jeres regler i Intune eller Defender',
    about: 'Enheden opfylder ikke en eller flere af jeres regler, eller sender ikke status til Defender. Årsagerne står på hver enhed.',
    recommend: 'Find ud af, hvilken regel enheden ikke opfylder, og ret det på enheden. Skal enheden ikke følge reglen, så undtag den.',
    from: [['enheder-Defender ikke onboardet', 'Defender ikke onboardet'], ['enheder-Fejlet compliance-politik', 'afviger fra en politik'], ['enheder-Compliance-afvigelse', 'markeret ikke i orden af Intune, uden årsag'], ['enheder-agent', 'ingen sikkerhedsstatus']] },
  { into: 'sp-review', tone: 'warn', area: 'sharepoint', title: 'Workspaces at gennemgå', hint: 'Med gæster, åbne for alle eller inaktive',
    about: 'Workspacet har eksterne gæster, er åbent for alle i organisationen, eller har ikke været brugt i over 90 dage. Det er ikke nødvendigvis forkert, men bør kunne forklares. Årsagerne står på hvert workspace.',
    recommend: 'Tjek, at gæsterne og åbenheden er ønsket, eller luk det, hvis det ikke bruges mere.',
    from: [['sp-guests', 'gæster har adgang'], ['sp-public', 'åbent for alle'], ['sp-inactive', 'inaktivt i over 90 dage']] },
  { into: 'exchange-shared-review', tone: 'warn', area: 'exchange', title: 'Delte postkasser at gennemgå', hint: 'Login slået til, eller adgang givet til slettede brugere',
    about: 'En delt postkasse bruges via medlemmernes egne konti og skal ikke kunne logges ind på direkte, og adgang givet til en slettet bruger skal fjernes. Årsagerne står på hver postkasse.',
    recommend: 'Bloker login på den delte postkasse, og fjern adgang givet til slettede brugere.',
    from: [['exchange-shared-signin', 'login slået til'], ['exchange-orphaned-access', 'adgang tildelt slettet bruger']] },
];
// Findings that are gone as headings of their own (their facts are Evidens on the object now) and the key they are read as.
const KEY_ALIASES = Object.fromEntries([
  ...MERGES.flatMap((m) => m.from.map(([key]) => [key, m.into])),
  ['id-old-password', 'id-mfa'],
  ['app-unverified', 'app-unused'],
]);
export const canonicalFindingKey = (key) => KEY_ALIASES[key] || key;

const mergeMeta = (a, b) => {
  const seen = new Set((a || []).map(([k]) => k));
  return [...(a || []), ...(b || []).filter(([k]) => !seen.has(k))];
};

export function consolidateGroups(groups) {
  const byKey = new Map(groups.map((g) => [g.key, g]));
  const merged = [];
  const done = new Set();
  groups.forEach((g) => {
    const spec = MERGES.find((m) => m.from.some(([key]) => key === g.key));
    if (!spec) { merged.push(g); return; }
    if (done.has(spec.into)) return;
    done.add(spec.into);
    const items = new Map();
    spec.from.forEach(([key, reason]) => {
      (byKey.get(key)?.items || []).forEach((it) => {
        const prev = items.get(it.id);
        const why = it.reasonText || reason;
        if (prev) { if (!prev.reasons.includes(why)) prev.reasons.push(why); prev.meta = mergeMeta(prev.meta, it.meta); } else items.set(it.id, { ...it, reasons: [why], meta: [...(it.meta || [])] });
      });
    });
    merged.push({
      key: spec.into, rank: spec.rank, tone: spec.tone, area: spec.area, title: spec.title, hint: spec.hint, about: spec.about, recommend: spec.recommend,
      items: [...items.values()].map(({ reasons, ...it }) => ({ ...it, why: reasons.join(' · '), meta: [['Årsager', reasons.join(' · ')], ...it.meta] })),
    });
  });
  // An object that is still in several findings stays in the most serious one (red before orange, then the order above).
  const order = merged.map((g, i) => ({ g, i })).filter(({ g }) => g.tone === 'bad' || g.tone === 'warn')
    .sort((a, b) => (a.g.tone === b.g.tone ? 0 : a.g.tone === 'bad' ? -1 : 1) || (a.g.rank || 0) - (b.g.rank || 0) || a.i - b.i);
  const seen = new Map();
  const kept = new Map();
  order.forEach(({ g }) => {
    const list = [];
    g.items.forEach((it) => {
      const id = `${g.area}::${it.id}`;
      const first = seen.get(id);
      if (first) { if (first.group !== g.key) first.also.push(g.title); return; }
      const rec = { item: it, group: g.key, also: [] };
      seen.set(id, rec);
      list.push(rec);
    });
    kept.set(g.key, list);
  });
  return merged.map((g) => {
    const list = kept.get(g.key);
    if (!list) return g;
    return { ...g, items: list.map((r) => (r.also.length ? { ...r.item, meta: [...(r.item.meta || []), ['Også fundet', [...new Set(r.also)].join(', ')]] } : r.item)) };
  });
}

// One of Microsoft's own system accounts (Exchange's HealthMailbox ..., Entra Connect's Sync_ ...) is not a person and cannot use MFA,
// so it is no finding. It only counts as one when the NAME is Microsoft's AND the data fits: an Exchange system account is synced from
// the customer's own Active Directory, holds no licence and has never signed in; an Entra Connect account is a cloud account on the
// onmicrosoft.com domain with no licence. Missing data (undefined) never makes an account a system account.
const EXCHANGE_SYSTEM_NAME = /^(HealthMailbox|SystemMailbox\{|DiscoverySearchMailbox|FederatedEmail\.)/i;
const CONNECT_SYSTEM_NAME = /^(Sync_.+|MSOL_[0-9a-f]+|AAD_[0-9a-f]+)$/i;
export function isMicrosoftSystemAccount(u) {
  if (u.licenseCount !== 0) return false;
  const name = String(u.displayName || '').trim();
  const local = String(u.userPrincipalName || '').split('@')[0];
  if (EXCHANGE_SYSTEM_NAME.test(name) || EXCHANGE_SYSTEM_NAME.test(local)) return u.onPremisesSyncEnabled === true && u.daysSinceSignIn === null;
  if (CONNECT_SYSTEM_NAME.test(name) || CONNECT_SYSTEM_NAME.test(local)) return /\.onmicrosoft\.com$/i.test(String(u.userPrincipalName || ''));
  return false;
}

// Mennesker & login — same STATUS_PREDICATE as IdentitetAdgang.jsx
const STATUS_PREDICATE = {
  mfa: (u) => u.personType === 'member' && u.accountEnabled !== false && !u.hasMfa,
  inactive: (u) => u.personType === 'member' && u.accountEnabled !== false && (u.daysSinceSignIn === null || u.daysSinceSignIn > 90),
};

// ctx: { mfa, risks, ca, devices, membership, insights, applications,
// security, sites, workspaces, licenses } — the same raw tenant data
// ArbejdskoPage.jsx already fetches; any caller wanting an accurate count
// has to fetch (or already have) the same sources, there's no way around
// that without re-introducing a second, approximate calculation.
export function useFindingGroups(ctx) {
  const { mfa, risks, ca, devices, membership, insights, applications, security, sites, workspaces, licenses } = ctx;

  // ---- Enheder ----
  const enhederGroups = useMemo(() => {
    if (!devices?.devices) return [];
    const enriched = devices.devices.map((d) => ({ ...d, daysSinceSync: daysSince(d.lastSyncDateTime) }));
    // A device Intune marks as compliant overall can still fail one policy (Konfiguration shows it under that policy as "Ikke opfyldt"
    // or "Fejl"). It is a finding too, so the same device is never in one place and missing in the other.
    // Only a state read fresh counts: when Microsoft would not answer this time, the earlier answer is kept for display but never
    // makes a finding (it can be days old, and is what made findings come and go).
    const failsAPolicy = (d) => !d.statesFromEarlierRead && (d.compliancePolicyStates || []).some((s) => ['nonCompliant', 'error', 'conflict'].includes(s.state));
    // A device that has not been in touch for over 30 days (Intune's own limit) or that Intune has not evaluated at all is not a device
    // that fails a rule: it has its own findings below, and is left out of the reasons (it has no real encryption or security data).
    const isStale = (d) => d.daysSinceSync != null && d.daysSinceSync > 30;
    const isUnevaluated = (d) => d.complianceState === 'unknown';
    const noncompliant = enriched.filter((d) => !isStale(d) && !isUnevaluated(d) && (d.complianceState !== 'compliant' || failsAPolicy(d)));
    const deviceAllReasons = new Map();
    const buckets = {};
    noncompliant.forEach((d) => {
      const reasons = d.complianceReasons && d.complianceReasons.length > 0 ? d.complianceReasons : [null];
      let classified = [...new Set(reasons.map((r) => (r === null ? (failsAPolicy(d) ? 'Fejlet compliance-politik' : 'Compliance-afvigelse') : classifyReason(r))))];
      // Intune itself says the disk is encrypted: a setting named BitLocker among the reasons then does not make it a missing
      // encryption. The device stays a finding, under what else fails.
      if (d.isEncrypted === true && classified.includes('Mangler kryptering')) {
        classified = classified.filter((b) => b !== 'Mangler kryptering');
        if (classified.length === 0) classified = [failsAPolicy(d) ? 'Fejlet compliance-politik' : 'Compliance-afvigelse'];
      }
      // Intune itself says the disk is not encrypted: that is the reason, even when the failing policy does not spell it out.
      if (d.isEncrypted === false && !classified.includes('Mangler kryptering')) classified.unshift('Mangler kryptering');
      deviceAllReasons.set(d.id, classified);
      // A device failing multiple checks appears in every matching category
      // now, not just the highest-priority one — per direct feedback, a
      // compliance product should surface every real deviation on an
      // object, not silently drop the second problem because a different
      // one already claimed it.
      classified.forEach((bucket) => {
        (buckets[bucket] = buckets[bucket] || []).push(d);
      });
    });
    const noThreatAgent = enriched.filter((d) => d.partnerReportedThreatState === 'unknown' && !isStale(d) && !isUnevaluated(d) && !deviceAllReasons.has(d.id));
    const toItem = (d, bucket) => {
      const platform = platformOf(d.operatingSystem);
      const failing = failingPolicies(d);
      const allFailing = failingAll(d);
      // The settings Intune names for this reason ('Ikke opfyldt: BitLocker, Secure Boot'); else the failing policies' names.
      const ofBucket = [...new Set((d.complianceReasons || []).filter((r) => classifyReason(r) === bucket).map(settingText))];
      // Hardware that cannot encrypt is told apart from a setting that is merely off: that is what an audit needs to know.
      const tpmMissing = typeof d.tpmVersion === 'string' && /^(unknown|none|0|0.0)$/i.test(d.tpmVersion.trim());
      const encryptionText = d.encryptionReadiness === 'notReady' ? `Hardwaren er ikke klar til kryptering${tpmMissing ? ' (TPM mangler eller er ukendt)' : ''}`
        : d.encryptionReadiness === 'ready' ? 'Klar til kryptering, men ikke slået til' : (NOT_ENCRYPTED_TEXT[platform] || 'Lagringen er ikke krypteret');
      const reasonText = bucket === 'Mangler kryptering' ? encryptionText
        : bucket === 'stale' || bucket === 'unevaluated' ? null
        : allFailing.length > 0 && bucket !== 'agent' ? `Afviger fra ${allFailing.length} ${allFailing.length === 1 ? 'politik' : 'politikker'}: ${nameList2(allFailing.map((s) => s.displayName))}`
        : ofBucket.length > 0 ? nameList2(ofBucket) : null;
      return {
      id: d.id, name: d.deviceName || 'Ukendt', sub: d.userDisplayName || '—', navPath: `enheder/${d.id}`,
      platform, platformLabel: platform ? PLATFORM_LABEL[platform] : null, reasonText,
      meta: [
        deviceOsRow(d),
        ...(failing.length > 0 ? [['Ikke opfyldt', failing.map((s) => `${s.displayName || 'En politik'} (${FAIL_STATE_TEXT[s.state]})`).join(' · ')]] : []),
        ...deviceReadyRows(d),
        ...(d.complianceReasons && d.complianceReasons.length > 0 ? [['Årsager fra Intune', d.complianceReasons.map(settingText).join(' · ')]] : []),
        deviceEncRow(d),
        deviceSyncRow(d),
      ],
      };
    };
    return [
      ...REASON_ORDER.map((key) => ({
        key: `enheder-${key}`, tone: REASON_TONE[key], area: 'devices', title: key, hint: REASON_HINT[key],
        about: REASON_ABOUT[key], recommend: REASON_RECOMMEND[key], items: (buckets[key] || []).map((d) => toItem(d, key)),
      })),
      { key: 'enheder-agent', tone: 'warn', area: 'devices', title: 'Ingen rapporteret sikkerhedsstatus', hint: 'Ingen sikkerhedsstatus endnu',
        about: 'Enheden har endnu ikke sendt en sikkerhedsstatus til Intune. Det kan være normalt for en helt ny enhed.', items: noThreatAgent.map((d) => toItem(d, 'agent')) },
      { key: 'enheder-stale', tone: 'warn', area: 'devices', title: 'Enheder, der ikke har meldt sig', hint: 'Ingen kontakt med Intune i over 30 dage',
        about: 'Enheden har ikke kontaktet Intune i over 30 dage, så vi ved ikke, om den lever op til jeres regler. Den kan være væk, slukket eller afleveret.',
        recommend: 'Bruges enheden ikke mere, så fjern den fra Intune, så den ikke står som en fejl. Bruges den, så få den online, så den kan synkronisere.',
        items: enriched.filter(isStale).map((d) => toItem(d, 'stale')) },
      { key: 'enheder-unevaluated', tone: 'warn', area: 'devices', title: 'Enheder uden compliance-vurdering', hint: 'Intune har ikke vurderet enheden',
        about: 'Intune har ikke vurderet enheden mod jeres regler (Not evaluated), fx fordi den ikke har en bruger eller ikke er fuldt tilmeldt. Derfor har vi hverken krypteringsstatus eller sikkerhedsstatus for den.',
        recommend: 'Tilmeld enheden korrekt i Intune, så den får en compliance-politik, eller fjern den, hvis den ikke skal bruges.',
        items: enriched.filter((d) => isUnevaluated(d) && !isStale(d)).map((d) => toItem(d, 'unevaluated')) },
    ];
  }, [devices]);

  // ---- Mennesker & login ----
  const identitetGroups = useMemo(() => {
    if (!mfa?.users) return [];
    const members = mergeMembers(mfa, risks);
    // Only trust "never signed in" when Microsoft gives sign-in data at all (it needs a licence): otherwise every account would look unused.
    const signInDataAvailable = members.some((u) => u.daysSinceSignIn != null);
    const isSystem = (u) => u.personType === 'member' && isMicrosoftSystemAccount(u);
    const neverSignedIn = (u) => signInDataAvailable && u.personType === 'member' && u.accountEnabled !== false && u.daysSinceSignIn === null && !isSystem(u);
    // A finding about a person: not a Microsoft system account, and someone who has signed in at least once.
    const isPerson = (u) => !isSystem(u) && !neverSignedIn(u);
    const guests = (risks?.guests || []).map((g) => ({
      id: g.id, displayName: g.displayName, personType: 'guest',
      daysSinceSignIn: daysSince(g.signInActivity?.lastSignInDateTime),
    }));
    const all = [...members, ...guests];
    const mfaEnforcedByPolicy = !!risks?.securityDefaultsEnabled || (ca?.policies || []).some(
      (p) => p.state === 'enabled' && p.grantControls?.builtInControls?.includes('mfa') && /alle brugere|all users/i.test(p.includeUsers || '')
    );
    const toItem = personItemOf;
    // ---- Entra ID Protection: accounts Microsoft itself has flagged as at risk ----
    const RISK_LABEL = { high: 'Høj', medium: 'Mellem', low: 'Lav' };
    const RISK_STATE_LABEL = { atRisk: 'Under risiko', confirmedCompromised: 'Bekræftet kompromitteret' };
    const riskyItems = (risks?.riskyUsers || []).map((r) => ({
      id: r.id, name: r.displayName || r.userPrincipalName || 'Ukendt', sub: 'Bruger i organisationen', navPath: `identitet/${r.id}`,
      meta: [
        ['Risikoniveau', RISK_LABEL[r.riskLevel] || 'Ikke tilgængeligt endnu'],
        ['Risikostatus', RISK_STATE_LABEL[r.riskState] || 'Ikke tilgængeligt endnu'],
        ['Sidst vurderet', ago(daysSince(r.riskLastUpdatedDateTime))],
      ],
    }));

    // ---- Only SMS/phone as the second step: the weakest MFA there is ----
    const WEAK_METHODS = new Set(['mobilePhone', 'alternateMobilePhone', 'officePhone', 'email']);
    const METHOD_LABEL = { mobilePhone: 'SMS/opkald', alternateMobilePhone: 'SMS/opkald', officePhone: 'Telefonopkald', email: 'E-mail' };
    const weakMfaItems = all
      .filter((u) => isPerson(u) && u.personType === 'member' && u.accountEnabled !== false && u.hasMfa && Array.isArray(u.methods) && u.methods.length > 0 && u.methods.every((m) => WEAK_METHODS.has(m)))
      .map((u) => ({
        id: u.id, name: u.displayName || 'Ukendt', sub: 'Bruger i organisationen', navPath: `identitet/${u.id}`,
        meta: [
          ['Registrerede metoder', [...new Set(u.methods.map((m) => METHOD_LABEL[m] || m))].join(', ')],
          ['Seneste login', ago(u.daysSinceSignIn)],
        ],
      }));

    // ---- Disabled accounts that still hold a paid licence ----
    const licenseNamesByUser = {};
    (licenses || []).forEach((sku) => (sku.users || []).forEach((lu) => {
      (licenseNamesByUser[lu.id] = licenseNamesByUser[lu.id] || []).push(sku.name);
    }));
    const disabledLicensedItems = (membership?.users || [])
      .filter((u) => u.accountEnabled === false && licenseNamesByUser[u.id])
      .map((u) => ({
        id: u.id, name: u.displayName || 'Ukendt', sub: 'Deaktiveret konto', navPath: `identitet/${u.id}`,
        meta: [
          ['Kontostatus', 'Deaktiveret'],
          ['Licens', licenseNamesByUser[u.id].join(', ')],
        ],
      }));

    return [
      { key: 'id-risky', tone: 'bad', area: 'identity', title: 'Brugere markeret som risiko', hint: 'Microsoft vurderer kontoen som udsat',
        about: 'Microsoft Entra ID Protection har markeret kontoen som mulig kompromitteret, fx efter login fra uvante steder eller en adgangskode, der er fundet lækket.',
        recommend: "Kontakt personen, og bekræft, at det var vedkommende, der loggede ind. Var det ikke, så nulstil adgangskoden, tving ny MFA-registrering, og luk aktive sessioner.",
        items: riskyItems },
      { key: 'id-weak-mfa', tone: 'warn', area: 'identity', title: 'Kun svag MFA-metode', hint: 'Kun SMS eller telefonopkald',
        about: 'Kontoen har kun SMS eller telefonopkald som ekstra loginbeskyttelse. Det er bedre end ingenting, men kan omgås, fx ved at overtage telefonnummeret.',
        recommend: 'Få brugeren til at tilføje Microsoft Authenticator eller en sikkerhedsnøgle.',
        items: weakMfaItems },
      { key: 'id-disabled-licensed', tone: 'warn', area: 'identity', title: 'Deaktiverede konti med licens', hint: 'Deaktiveret, men stadig betalt',
        about: 'Kontoen er deaktiveret, men har stadig en licens. I betaler for en licens, som ingen bruger.',
        recommend: "Fjern licensen fra kontoen, hvis den ikke skal bruges igen.",
        items: disabledLicensedItems },
      { key: 'id-mfa', tone: 'bad', area: 'identity', title: 'Ingen MFA eller totrinsgodkendelse', hint: 'Ingen MFA på kontoen',
        about: mfaEnforcedByPolicy
          ? 'Microsoft viser ikke totrinsgodkendelse (MFA) på kontoen. MFA betyder, at man også skal godkende login på sin telefon. En loginregel kræver MFA af alle.'
          : 'Microsoft viser ikke totrinsgodkendelse (MFA) på kontoen. MFA betyder, at man også skal godkende login på sin telefon.',
        recommend: mfaEnforcedByPolicy
          ? "Tjek, om brugeren er undtaget fra loginreglen, og sæt MFA op på kontoen."
          : "Tjek, om brugeren har MFA, eller om en loginregel dækker kontoen. Sæt ellers MFA op.",
        items: all.filter((u) => STATUS_PREDICATE.mfa(u) && isPerson(u)).map(toItem) },
      { key: 'id-inactive', tone: 'warn', area: 'identity', title: 'Inaktive konti', hint: 'Intet login i over 90 dage',
        about: 'Der er ikke registreret login på kontoen i over 90 dage.',
        recommend: "Spørg personen eller dennes leder, om kontoen stadig skal bruges. Hvis ikke, så bloker kontoen, og slet den, når I er sikre.",
        // No longer excludes accounts also missing MFA — per direct
        // feedback, an account failing both checks is a genuine finding in
        // both categories, not just whichever one used to win.
        items: all.filter((u) => STATUS_PREDICATE.inactive(u) && isPerson(u)).map(toItem) },
      { key: 'id-never', tone: 'neutral', area: 'identity', title: 'Konti, der aldrig har logget ind', hint: 'Intet registreret login',
        about: 'Kontoen har aldrig logget ind. Det er ofte en tjenestekonto, som ikke kan bruge MFA, men det kan også være en konto, der er oprettet og aldrig brugt. Derfor tæller den ikke med under manglende MFA.',
        recommend: 'Find ud af, hvad kontoen bruges til. Hører den til et system, så dokumentér det. Bruges den ikke, så deaktiver eller fjern den.',
        items: all.filter(neverSignedIn).map((u) => { const it = toItem(u); return { ...it, sub: 'Aldrig logget ind', meta: it.meta.map(([k, v]) => (k === 'Seneste login' ? [k, 'Aldrig'] : [k, v])) }; }) },
      { key: 'id-guests', tone: 'neutral', area: 'identity', title: 'Gæstebrugere', hint: 'Personer uden for organisationen',
        about: 'Gæster er personer uden for organisationen, som er inviteret ind. Det er almindeligt ved samarbejde med kunder og leverandører.',
        items: all.filter((u) => u.personType === 'guest' && u.daysSinceSignIn !== null && u.daysSinceSignIn <= 90).map(toItem) },
    ];
  }, [mfa, risks, ca, licenses, membership]);

  // ---- E-mail ----
  const exchangeGroups = useMemo(() => {
    if (!membership?.users) return [];
    const forwardsByMailbox = {};
    (insights?.forwardingRules || []).forEach((r) => { if (r.isExternal) forwardsByMailbox[(r.mailbox || '').toLowerCase()] = r.forwardsTo; });
    const externalForwards = membership.users.filter((u) => forwardsByMailbox[(u.userPrincipalName || '').toLowerCase()]);

    // Full Access/Send As granted to a trustee Exchange can no longer
    // resolve to a real account — almost always a former employee whose
    // account was deleted without their mailbox delegate access being
    // cleaned up first. getMailboxDelegates (backend) relabels an
    // unresolvable trustee's raw SID as "Slettet bruger".
    const orphanedByMailbox = new Map();
    membership.users.forEach((u) => {
      const key = (u.userPrincipalName || '').toLowerCase();
      const delegates = insights?.mailboxDelegates?.[key];
      if (!delegates) return;
      const count = [...(delegates.fullAccess || []), ...(delegates.sendAs || [])].filter((v) => v === 'Slettet bruger').length;
      if (count > 0) orphanedByMailbox.set(u.id, count);
    });

    // ---- Domains without full SPF/DMARC protection (one finding item per domain) ----
    const domainList = insights?.emailAuthDomains || (insights?.emailAuth ? [insights.emailAuth] : []);
    const domainItem = (d) => ({
      id: `domain:${d.domain}`, name: d.domain, sub: 'E-mail-domæne', navPath: 'exchange', domainAuth: d,
      meta: domainMeta(d),
    });
    const domainsMissing = domainList.filter((d) => !d.spf?.configured || !d.dmarc?.configured).map(domainItem);
    const domainsWeak = domainList.filter((d) => d.spf?.configured && d.dmarc?.configured && d.dmarc.policy === 'none').map(domainItem);

    // ---- Shared mailboxes that can still be signed in to directly ----
    const sharedSignIn = membership.users.filter((u) => {
      const type = insights?.recipientTypes?.[(u.userPrincipalName || '').toLowerCase()]?.type;
      return type === 'SharedMailbox' && u.accountEnabled !== false;
    });


    return [
      { key: 'exchange-domain-missing', tone: 'bad', area: 'exchange', title: 'Domæner uden SPF eller DMARC', hint: 'Andre kan sende mail i jeres navn',
        about: 'SPF og DMARC fortæller modtagerne, hvilke servere der må sende mail fra domænet. Mangler en af dem, kan andre udgive sig for at være jer.',
        recommend: 'Opret manglende SPF- og DMARC-poster hos jeres DNS-udbyder.',
        items: domainsMissing },
      { key: 'exchange-domain-weak', tone: 'warn', area: 'exchange', title: 'DMARC uden håndhævelse', hint: 'DMARC er sat til kun at overvåge',
        about: 'DMARC er oprettet, men med politikken "none". Den overvåger kun og stopper ikke falske mails.',
        recommend: 'Når rapporterne ser fornuftige ud, skift politikken til "quarantine" eller "reject".',
        items: domainsWeak },
      { key: 'exchange-shared-signin', tone: 'warn', area: 'exchange', title: 'Delte postkasser med login slået til', hint: 'Man kan logge direkte ind på en delt postkasse',
        about: 'En delt postkasse bruges via medlemmernes egne konti og skal ikke kunne logges ind på direkte. Er login slået til, er det en ekstra konto uden MFA, som kan angribes.',
        recommend: 'Bloker login på den delte postkasse.',
        items: sharedSignIn.map((m) => ({
          id: m.id, name: m.displayName || 'Ukendt', sub: m.mail || m.userPrincipalName, navPath: `identitet/${m.id}`,
          meta: [['Type', 'Delt postkasse'], ['Kontostatus', 'Login aktivt']],
        })) },
      { key: 'exchange-forward', tone: 'bad', area: 'exchange', title: 'Postkasser med ekstern videresendelse', hint: 'Mails sendes videre uden for organisationen',
        about: 'Postkassen sender mails videre til en adresse uden for organisationen. Det er nogle gange ønsket, men bruges også til at læse med i det skjulte.',
        recommend: "Spørg postkassens ejer, om videresendelsen er ønsket. Hvis ikke, så fjern den.",
        items: externalForwards.map((m) => ({
          id: m.id, name: m.displayName || 'Ukendt', sub: m.mail || m.userPrincipalName, navPath: `identitet/${m.id}`,
          meta: [['Videresendes til', forwardsByMailbox[(m.userPrincipalName || '').toLowerCase()] || 'Ikke tilgængeligt endnu']],
        })) },
      { key: 'exchange-orphaned-access', tone: 'warn', area: 'exchange', title: 'Postkasser med adgang tildelt slettede brugere', hint: 'Adgang givet til en bruger, der ikke findes',
        about: 'Postkassen har givet adgang til en bruger, som ikke længere findes.',
        recommend: "Fjern adgangen, hvis den ikke bruges mere.",
        items: membership.users.filter((m) => orphanedByMailbox.has(m.id)).map((m) => ({
          id: m.id, name: m.displayName || 'Ukendt', sub: m.mail || m.userPrincipalName, navPath: `identitet/${m.id}`,
          meta: [['Adgange fra slettede brugere', String(orphanedByMailbox.get(m.id))]],
        })) },
    ];
  }, [membership, insights]);

  // ---- Applikationer ----
  const appGroups = useMemo(() => {
    if (!applications?.applications) return [];
    const thirdParty = applications.applications.filter((a) => !a.isMicrosoftApp).map(prepApp);
    const hasWrite = appHasWrite;
    const toItem = appItemOf;
    // Secret/certificate wording comes from the backend as "Udløber om 12 dage" / "Udløbet for 6 dage siden".
    const expiryOf = (text) => {
      const expired = /^Udløbet for (\d+) dage? siden/.exec(text || '');
      if (expired) return { expired: true, days: Number(expired[1]) };
      const soon = /^Udløber om (\d+) dage?/.exec(text || '');
      return soon ? { expired: false, days: Number(soon[1]) } : null;
    };
    const credentialRows = (a) => {
      const rows = [];
      const secret = expiryOf(a.secretExpiry);
      const cert = expiryOf(a.certificateStatus);
      if (secret) rows.push(['Secret nøgle', a.secretExpiry]);
      if (cert) rows.push(['Certifikat', a.certificateStatus]);
      return rows;
    };
    const expiredApps = thirdParty.filter((a) => expiryOf(a.secretExpiry)?.expired || expiryOf(a.certificateStatus)?.expired);
    const expiringApps = thirdParty.filter((a) => {
      if (expiredApps.includes(a)) return false;
      const s = expiryOf(a.secretExpiry);
      const c = expiryOf(a.certificateStatus);
      return (s && s.days <= 30) || (c && c.days <= 30);
    });
    const writeApps = thirdParty.filter(hasWrite);
    const credItem = (a) => {
      const it = toItem(a);
      return { ...it, why: credentialRows(a).map(([k, v]) => `${k}: ${v.toLowerCase()}`).join(' · '), meta: [...credentialRows(a), ...it.meta] };
    };
    // An app only counts as unused when Microsoft really has told us about its use, the app is older than 90 days, and Microsoft
    // answers for most of the tenant's apps (otherwise the answer is not to be trusted, and nothing is flagged).
    const answered = thirdParty.filter((a) => a.lastSignInAvailable === true);
    const usageTrusted = thirdParty.length > 0 && answered.length / thirdParty.length >= 0.5;
    const unusedApps = usageTrusted ? answered.filter((a) => (a.daysSinceUsed === null || a.daysSinceUsed > 90) && (a.ageDays == null || a.ageDays > 90)) : [];
    const noUsageData = thirdParty.filter((a) => a.lastSignInAvailable !== true);

    return [
      { key: 'app-secret-expired', tone: 'bad', area: 'applications', title: 'Udløbede hemmeligheder', hint: 'Nøgle eller certifikat er udløbet',
        about: 'Appens hemmelige nøgle eller certifikat er udløbet. Appen kan ikke længere logge ind, og integrationen holder op med at virke, indtil den fornyes.',
        recommend: "Bruges appen stadig, så opret en ny nøgle eller et nyt certifikat, og læg det ind, hvor appen bruges. Kender I ikke appen, eller bruges den ikke, så deaktivér eller slet den.",
        items: expiredApps.map(credItem) },
      { key: 'app-secret-expiring', tone: 'warn', area: 'applications', title: 'Hemmeligheder der udløber snart', hint: 'Udløber inden for 30 dage',
        about: 'Appens hemmelige nøgle eller certifikat udløber inden for 30 dage. Fornyes den ikke i tide, stopper integrationen.',
        recommend: "Opret en ny nøgle eller et nyt certifikat, inden den gamle udløber. Bruges appen ikke længere, så slet den i stedet.",
        items: expiringApps.map(credItem) },
      { key: 'app-write-all', tone: 'bad', area: 'applications', title: 'Apps med skriveadgang til hele tenanten', hint: 'Kan ændre eller sende i stor skala',
        about: 'Appen har tilladelser, der kan ændre eller sende data for alle, fx at skrive til alle filer, brugere eller sende mail. Bruges de forkert, er skaden stor.',
        recommend: "Er skriveadgangen ikke nødvendig, eller kender I ikke appen, så fjern tilladelsen, eller deaktivér og slet appen.",
        items: writeApps.map(toItem) },
      { key: 'app-priv', tone: 'bad', area: 'applications', title: 'Apps med vidtgående permissions', hint: '10 eller flere tilladelser',
        about: 'Appen har 10 eller flere tilladelser hos jer. Det er ikke nødvendigvis forkert, men det giver appen bred adgang.',
        recommend: "Kender I ikke appen, eller bruger I den ikke, så deaktivér eller slet den. Ellers fjern de tilladelser, den ikke har brug for.",
        items: thirdParty.filter((a) => a.permissionCount >= 10).map(toItem) },
      { key: 'app-unused', tone: 'warn', area: 'applications', title: 'Apps uden registreret brug', hint: 'Ingen login registreret i over 90 dage',
        about: 'Microsoft har ikke registreret, at appen er brugt i over 90 dage. Det betyder ikke med sikkerhed, at den er ubrugt, kun at Microsoft ikke har set den. Appen har stadig sine tilladelser.',
        recommend: "Skal appen ikke bruges mere, eller kender I den ikke, så deaktivér eller slet den.",
        items: unusedApps.map((a) => ({ ...toItem(a), why: 'ingen registreret brug i over 90 dage' })) },
      { key: 'app-usage-unknown', tone: 'neutral', area: 'applications', title: 'Apps uden brugsdata', hint: 'Microsoft oplyser ikke, om appen bruges',
        about: 'Microsoft har ikke kunnet oplyse, hvornår appen sidst blev brugt, så vi kan ikke sige, om den er ubrugt. Derfor tæller de ikke som fund.',
        items: noUsageData.map((a) => ({ ...toItem(a), why: 'Microsoft oplyser ikke brug' })) },
    ];
  }, [applications]);

  // ---- Administratorroller ----
  const rollerGroups = useMemo(() => {
    if (!risks?.privilegedRoles) return [];
    const mfaById = new Map((mfa?.users || []).map((u) => [u.id, u.hasMfa]));
    const byRole = new Map();
    risks.privilegedRoles.forEach((r) => {
      if (!byRole.has(r.role)) byRole.set(r.role, []);
      if (r.status !== 'eligible' && r.status !== 'activated') byRole.get(r.role).push(r);
    });
    const globalItems = [];
    const otherItems = [];
    byRole.forEach((holders, role) => {
      if (holders.length === 0) return;
      const missingMfa = holders.filter((h) => h.principalType === 'Bruger' && mfaById.get(h.principalId) === false).length;
      const item = { id: role, name: role, sub: `${holders.length} ${holders.length === 1 ? 'person' : 'personer'}${missingMfa > 0 ? ` · ${missingMfa} uden MFA` : ''}`, navPath: `roller/${encodeURIComponent(role)}` };
      if (role === 'Global Administrator') globalItems.push(item);
      else otherItems.push(item);
    });
    const permanent = risks.privilegedRoles.filter((r) => r.status !== 'eligible' && r.status !== 'activated');
    const signInById = new Map((risks.memberSignIns || []).map((u) => [u.id, u]));
    // One item per role, listing the holders that match — the role is the object Arbejdskø opens.
    const roleItems = (pred, describe) => {
      const map = new Map();
      permanent.filter(pred).forEach((r) => {
        if (!map.has(r.role)) map.set(r.role, []);
        map.get(r.role).push(r);
      });
      return [...map.entries()].map(([role, holders]) => ({
        id: role, name: role, sub: describe(holders), navPath: `roller/${encodeURIComponent(role)}`,
      }));
    };
    const people = (n) => `${n} ${n === 1 ? 'person' : 'personer'}`;
    const noMfaItems = roleItems(
      (r) => r.principalType === 'Bruger' && !r.isGuest && mfaById.get(r.principalId) === false,
      (h) => `${people(h.length)} uden MFA: ${nameList(h.map((x) => x.displayName))}`
    );
    const guestAdminItems = roleItems((r) => !!r.isGuest, (h) => `${people(h.length)}: ${nameList(h.map((x) => x.displayName))}`);
    const inactiveAdminItems = roleItems(
      (r) => r.principalType === 'Bruger' && !r.isGuest && (signInById.get(r.principalId)?.daysSinceSignIn ?? 0) > 30,
      (h) => `${people(h.length)} uden login i 30+ dage: ${nameList(h.map((x) => x.displayName))}`
    );
    const appAdminItems = roleItems((r) => r.principalType === 'App', (h) => `${h.length} ${h.length === 1 ? 'app' : 'apps'}: ${nameList(h.map((x) => x.displayName))}`);

    return [
      { key: 'roller-no-mfa', tone: 'bad', area: 'roles', title: 'Administratorer uden MFA', hint: 'Privilegeret konto uden ekstra loginbeskyttelse',
        about: 'En person med administratorrolle har ingen MFA. Overtager en angriber kontoen, får vedkommende administratorrettigheder uden yderligere forhindringer.',
        recommend: 'Få personen til at sætte MFA op med det samme. Er det en nødkonto, så beskyt den med en nøgle i stedet for et kodeord alene.',
        items: noMfaItems },
      { key: 'roller-guest-admin', tone: 'bad', area: 'roles', title: 'Gæster med administratorrolle', hint: 'En ekstern person har administratorrettigheder',
        about: 'En gæst, altså en person uden for organisationen, har en administratorrolle. Det er sjældent nødvendigt, og I styrer ikke gæstens egen sikkerhed.',
        recommend: 'Fjern rollen fra gæsten, eller bekræft, at det er aftalt, fx med en ekstern IT-leverandør.',
        items: guestAdminItems },
      { key: 'roller-inactive-admin', tone: 'warn', area: 'roles', title: 'Inaktive administratorer', hint: 'Ingen login i over 30 dage',
        about: 'En person med administratorrolle har ikke logget ind i over 30 dage. Rettighederne er der stadig, selvom ingen bruger dem.',
        recommend: 'Tjek, om personen stadig skal være administrator. Ellers fjern rollen.',
        items: inactiveAdminItems },
      { key: 'roller-app-admin', tone: 'warn', area: 'roles', title: 'Apps med administratorrolle', hint: 'En app har administratorrettigheder',
        about: 'En app har fået en administratorrolle. Apps har ikke MFA, så rettigheden er kun så sikker som appens egen nøgle.',
        recommend: 'Tjek, at appen skal have rollen, og at nøglen opbevares sikkert.',
        items: appAdminItems },
      { key: 'roller-global', tone: 'bad', area: 'roles', title: 'Permanent Global Administrator', hint: 'Har rollen hele tiden',
        about: 'Personen har rollen Global Administrator hele tiden. Det er Microsofts mest omfattende rolle, og Microsoft anbefaler få personer, typisk 2 til 4. Rollen kan gøres midlertidig (PIM), så den kun er slået til, når den skal bruges.',
        items: globalItems },
      { key: 'roller-other', tone: 'neutral', area: 'roles', title: 'Andre rolletildelinger', hint: 'Særlige rettigheder',
        about: 'Personer med særlige rettigheder, fx til brugere, mails eller sikkerhed. Det er normalt, at nogle har det.',
        items: otherItems },
    ];
  }, [risks, mfa]);

  // ---- Loginregler ----
  const caGroups = useMemo(() => {
    if (!ca?.policies) return [];
    // Short and structural instead of the old p.summary sentence — that
    // sentence used to repeat verbatim under the name AND as "Hvorfor det
    // er vigtigt" AND partly again in Evidens (direct feedback). No `why`
    // override here any more either: the group's own short `hint` below
    // ("Håndhæver intet endnu"/"Håndhæver intet") already says the one
    // thing that matters for these two finding groups.
    const toItem = (p) => {
      const controlsShort = p.controlsShort || [];
      const controlsLabel = controlsShort.length > 0 ? controlsShort.join(', ') : 'Intet konfigureret';
      return {
        id: p.id,
        name: p.displayName,
        rawName: p.displayName,
        sub: controlsShort.length > 0 ? `Kræver ${controlsLabel}` : 'Ingen adgangskrav konfigureret',
        navPath: `conditional-access/${p.id}`,
        meta: [
          ['Status', p.state === 'disabled' ? 'Deaktiveret' : p.state === 'enabledForReportingButNotEnforced' ? 'Kun rapportering' : 'Aktiveret'],
          ['Gælder for', p.targetSummaryShort || 'Alle brugere'],
          ['Kræver', controlsLabel],
        ],
        controlsShort,
        excludeSummary: p.excludeSummaryShort || null,
        sessionControlsSummary: p.sessionControlsSummary || [],
        riskLevels: p.riskLevels || { signIn: [], user: [] },
      };
    };
    return [
      { key: 'ca-report', tone: 'warn', area: 'conditional-access', title: 'Kun rapportering', hint: 'Håndhæver ikke noget endnu',
        about: 'Reglen er sat til kun at rapportere. Den registrerer, hvad den ville have gjort, men håndhæver ikke noget.',
        recommend: "Se i rapporten, hvad reglen ville ramme. Ser det rigtigt ud, så skift reglen fra Report-only til On.",
        items: ca.policies.filter((p) => p.state === 'enabledForReportingButNotEnforced').map(toItem) },
      { key: 'ca-disabled', tone: 'bad', area: 'conditional-access', title: 'Deaktiverede loginregler', hint: 'Slået fra',
        about: 'Reglen er slået fra og har ingen effekt lige nu.',
        recommend: "Slå reglen til, eller slet den, hvis den ikke skal bruges.",
        items: ca.policies.filter((p) => p.state === 'disabled').map(toItem) },
    ];
  }, [ca]);

  // ---- Sikkerhed & trusler ----
  const sikkerhedGroups = useMemo(() => {
    if (!security) return [];
    const isResolved = (status) => (status || '').toLowerCase() === 'resolved';
    const severityMeta = (x) => [
      ['Alvorlighed', x.severity || 'Ikke tilgængeligt endnu'],
      ['Status', x.status || 'Ikke tilgængeligt endnu'],
    ];
    const all = [
      ...(security.incidents || []).map((i) => ({ id: i.id, name: i.displayName || 'Unavngivet incident', sub: 'Incident', severity: i.severity, status: i.status, createdDateTime: i.createdDateTime, navPath: `sikkerhed/incident/${i.id}`, meta: severityMeta(i) })),
      ...(security.alerts || []).map((a) => ({ id: a.id, name: a.title || 'Unavngivet alert', sub: 'Alert', severity: a.severity, status: a.status, createdDateTime: a.createdDateTime, navPath: `sikkerhed/alert/${a.id}`, meta: severityMeta(a) })),
    ];
    const open = all.filter((x) => !isResolved(x.status));
    const staleOpen = open.filter((x) => daysSince(x.createdDateTime) > 14);
    const placeOf = (e) => [e.city, e.country].filter(Boolean).join(', ') || 'Ikke tilgængeligt endnu';
    const riskySignIns = (security.riskySignIns || []).map((e) => ({
      id: e.id, name: e.displayName || e.userPrincipalName || 'Ukendt', sub: 'Login med risiko', affectedUserId: e.userId, navPath: `identitet/${e.userId}`,
      meta: [['Alvorlighed', e.riskLevel || 'medium'], ['Sted', placeOf(e)], ['App', e.app || 'Ikke tilgængeligt endnu'], ['Tidspunkt', ago(daysSince(e.createdDateTime))]],
      description: `Microsoft vurderer, at et login med ${e.app || 'en app'} fra ${placeOf(e)} har ${e.riskLevel === 'high' ? 'høj' : 'middel'} risiko for at være en angriber.`,
    }));
    const failedSignIns = (security.failedSignInUsers || []).map((g) => ({
      id: `failed:${g.userId || g.userPrincipalName}`, name: g.displayName || g.userPrincipalName || 'Ukendt', sub: 'Mange mislykkede login', affectedUserId: g.userId || null, navPath: g.userId ? `identitet/${g.userId}` : 'identitet',
      meta: [['Alvorlighed', 'medium'], ['Mislykkede forsøg', String(g.count)], ['Lande', (g.countries || []).join(', ') || 'Ikke tilgængeligt endnu'], ['Seneste forsøg', ago(daysSince(g.lastDateTime))]],
      description: `${g.count} mislykkede loginforsøg de seneste 7 dage${(g.countries || []).length > 1 ? ` fra ${g.countries.length} lande` : ''}. Mange forsøg efter hinanden ligner, at nogen gætter adgangskoden.`,
    }));
    const legacyAuth = (security.legacyAuthUsers || []).map((g) => ({
      id: `legacy:${g.userId || g.userPrincipalName}`, name: g.displayName || g.userPrincipalName || 'Ukendt', sub: 'Forældet godkendelse', affectedUserId: g.userId || null, navPath: g.userId ? `identitet/${g.userId}` : 'identitet',
      meta: [['Alvorlighed', 'medium'], ['Protokoller', (g.protocols || []).join(', ') || 'Ikke tilgængeligt endnu'], ['Antal login', String(g.count)], ['Seneste login', ago(daysSince(g.lastDateTime))]],
      description: `${g.count} login de seneste 7 dage med ${(g.protocols || []).join(', ')}. Forældede protokoller kan ikke kræve MFA, så de omgår jeres loginregler.`,
    }));

    return [
      { key: 'sik-risky-signin', tone: 'bad', area: 'security', title: 'Mistænkelige login', hint: 'Microsoft vurderer, at loginet kan være en angriber',
        about: 'Microsoft Entra ID Protection har markeret et login som risikabelt, fx fra et uvant sted eller en kendt angribers adresse.',
        recommend: "Tjek med brugeren, om loginet var vedkommende selv. Hvis ikke, nulstil adgangskoden.",
        items: riskySignIns },
      { key: 'sik-failed-signins', tone: 'warn', area: 'security', title: 'Mange mislykkede login', hint: '10 eller flere mislykkede forsøg på 7 dage',
        about: 'Der har været mange mislykkede loginforsøg på kontoen. Det kan være en bruger, der har glemt sin adgangskode, men også en angriber, der gætter.',
        recommend: 'Tjek, om brugeren selv har forsøgt. Ellers overvej at nulstille adgangskoden.',
        items: failedSignIns },
      { key: 'sik-legacy-auth', tone: 'warn', area: 'security', title: 'Login med forældet godkendelse', hint: 'IMAP, POP eller SMTP uden MFA',
        about: 'Kontoen har logget ind med en ældre protokol som IMAP, POP eller SMTP. De kan ikke bruge MFA, så de er en vej uden om jeres loginregler.',
        recommend: "Find ud af, hvilken app der bruger protokollen, og skift den til moderne login. Blokér derefter forældet godkendelse med en Conditional Access-regel.",
        items: legacyAuth },
      { key: 'sik-stale-open', tone: 'warn', area: 'security', title: 'Åbne sager uden opfølgning', hint: 'Åben i over 14 dage',
        about: 'Sagen har været åben i over 14 dage. En sikkerhedshændelse, der ikke bliver fulgt op, risikerer at blive glemt.',
        recommend: "Tildel en ansvarlig, undersøg sagen, og luk den.",
        items: staleOpen.map((x) => ({ ...x, meta: [...x.meta, ['Oprettet', ago(daysSince(x.createdDateTime))]] })) },
      { key: 'sik-high', tone: 'bad', area: 'security', title: 'Høj alvor', hint: 'Åben sag med høj alvor',
        about: 'Microsoft Defender har en åben sag med høj alvor.',
        recommend: "Undersøg sagen, og luk den, når den er håndteret.",
        items: open.filter((x) => (x.severity || '').toLowerCase() === 'high') },
      { key: 'sik-other', tone: 'warn', area: 'security', title: 'Øvrige åbne', hint: 'Åbne sager med lavere alvor',
        about: 'Der er åbne sager med lavere alvor i Defender.',
        recommend: "Gennemgå dem, og luk dem, der er håndteret eller falske alarmer.",
        items: open.filter((x) => (x.severity || '').toLowerCase() !== 'high') },
    ];
  }, [security]);

  // ---- Filer & samarbejde ----
  const sharepointGroups = useMemo(() => {
    if (!sites && !workspaces) return [];
    const enrichedSites = (sites || []).map((s) => ({ ...s, daysSinceActivity: daysSince(s.lastActivityDateTime) }));
    const enrichedWorkspaces = (workspaces || []).map((w) => ({ ...w, daysSinceActivity: daysSince(w.lastActivityDateTime) }));
    // Workspaces with external guests among their members — the detail panel lists every member and
    // marks the guests, since "who from outside can see this" is the question an auditor asks first.
    const guestSub = (w) => (w.resources || []).join(' + ') || 'Microsoft 365-gruppe';
    const guestItem = (w) => ({
      id: w.id, name: w.displayName, sub: guestSub(w), navPath: `teams/${w.id}`,
      meta: [
        ['Gæster', String(w.guestCount ?? 0)],
        ['Medlemmer', w.memberCount != null ? String(w.memberCount) : 'Ikke tilgængeligt endnu'],
        ['Seneste aktivitet', ago(w.daysSinceActivity)],
      ],
    });
    const withGuests = enrichedWorkspaces.filter((w) => (w.guestCount || 0) > 0);
    const guestsStale = withGuests.filter((w) => w.daysSinceActivity === null || w.daysSinceActivity > 90);
    const openWorkspaces = enrichedWorkspaces.filter((w) => w.visibility === 'Public');

    return [
      { key: 'sp-guests-stale', tone: 'bad', area: 'sharepoint', title: 'Gæster i inaktive workspaces', hint: 'Eksterne har adgang til noget, ingen bruger',
        about: 'Eksterne gæster har adgang til et workspace, hvor der ikke har været aktivitet i over 90 dage. Adgangen står åben, men ingen holder øje.',
        recommend: 'Fjern gæsterne, eller luk workspacet, hvis det ikke skal bruges mere.',
        items: guestsStale.map(guestItem) },
      { key: 'sp-guests', tone: 'warn', area: 'sharepoint', title: 'Workspaces med gæster', hint: 'Eksterne personer er medlemmer',
        about: 'Der er eksterne gæster blandt medlemmerne. Det er almindeligt ved samarbejde med kunder og leverandører, men bør kunne forklares og gennemgås.',
        recommend: 'Tjek, at hver gæst stadig skal have adgang.',
        items: withGuests.map(guestItem) },
      { key: 'sp-public', tone: 'warn', area: 'sharepoint', title: 'Åbne workspaces', hint: 'Alle i organisationen kan melde sig ind',
        about: 'Workspacet er åbent, så alle i organisationen selv kan melde sig ind og se indholdet. Det passer dårligt til fortroligt materiale.',
        recommend: 'Gør workspacet privat, hvis indholdet ikke skal være åbent for alle.',
        items: openWorkspaces.map((w) => ({
          id: w.id, name: w.displayName, sub: guestSub(w), navPath: `teams/${w.id}`,
          meta: [['Synlighed', 'Åben for alle i organisationen'], ['Medlemmer', w.memberCount != null ? String(w.memberCount) : 'Ikke tilgængeligt endnu'], ['Seneste aktivitet', ago(w.daysSinceActivity)]],
        })) },
      { key: 'sp-inactive', tone: 'warn', area: 'sharepoint', title: 'Inaktive sites', hint: 'Ingen aktivitet i over 90 dage',
        about: 'Der er ikke registreret aktivitet i over 90 dage. Filerne ligger stadig der.',
        recommend: 'Spørg ejeren, om sitet stadig skal bruges. Hvis ikke, så arkivér eller slet det, så ingen længere har adgang til gamle filer.',
        items: [
          ...enrichedSites.filter((s) => s.daysSinceActivity === null || s.daysSinceActivity > 90).map((s) => ({
            id: s.id, name: s.displayName, sub: s.siteType || 'SharePoint-site', navPath: `sharepoint/${s.id}`,
            meta: [['Seneste aktivitet', s.daysSinceActivity == null ? 'Ikke tilgængeligt endnu' : `${s.daysSinceActivity} dage siden`]],
          })),
          ...enrichedWorkspaces.filter((w) => w.daysSinceActivity === null || w.daysSinceActivity > 90).map((w) => ({
            id: w.id, name: w.displayName, sub: (w.resources || []).join(' + ') || 'Microsoft 365-gruppe', navPath: `teams/${w.id}`,
            meta: [['Seneste aktivitet', w.daysSinceActivity == null ? 'Ikke tilgængeligt endnu' : `${w.daysSinceActivity} dage siden`]],
          })),
        ] },
      // A Microsoft 365-workspace with no registered owner is a real
      // governance gap — per direct feedback, the owner is "normally who
      // the customer needs to contact", so a workspace with none is
      // genuinely harder to govern, not just a cosmetic gap.
      { key: 'sp-no-owner', tone: 'warn', area: 'sharepoint', title: 'Intet registreret ejerskab', hint: 'Ingen registreret ejer',
        about: 'Microsoft viser ingen registreret ejer på workspacet. Ejere styrer som standard medlemmer og indstillinger. Andre, fx administratorer, kan stadig have adgang. Det kan vi ikke se her.',
        recommend: 'Udpeg mindst to ejere. Bruges workspacet ikke, så slet det.',
        items: (workspaces || []).filter((w) => !w.owners || w.owners.length === 0).map((w) => ({
          id: w.id, name: w.displayName, sub: (w.resources || []).join(' + ') || 'Microsoft 365-gruppe', navPath: `teams/${w.id}`,
          meta: [['Medlemmer', w.memberCount != null ? `${w.memberCount}` : 'Ikke tilgængeligt endnu']],
        })) },
    ];
  }, [sites, workspaces]);

  // ---- Licenser ----
  const licenserGroups = useMemo(() => {
    if (!licenses) return [];
    const enriched = licenses.map((l) => ({ ...l, unused: Math.max(l.total - l.assigned, 0) }));
    return [
      { key: 'lic-unused', tone: 'neutral', area: 'licenses', title: 'Ledig licenskapacitet', hint: 'Licenser der ikke er tildelt',
        about: 'I har købt flere licenser, end der er tildelt. Det er ikke nødvendigvis et problem, fx hvis I venter nye medarbejdere.',
        items: enriched.filter((l) => l.unused > 0 && !l.addon).map((l) => ({
          id: l.skuId, name: l.name, sub: `${l.unused} ledige af ${l.total}`, navPath: `licenser/${l.skuId}`,
          meta: [['Produkt', l.name], ...(l.sku ? [['SKU', l.sku]] : []), ['Købt', `${l.total}`], ['Tildelt', `${l.assigned}`], ['Ledige', `${l.unused}`]],
        })) },
    ];
  }, [licenses]);

  const allGroups = useMemo(
    () => consolidateGroups([...identitetGroups, ...enhederGroups, ...exchangeGroups, ...appGroups, ...rollerGroups, ...caGroups, ...sikkerhedGroups, ...sharepointGroups, ...licenserGroups]),
    [identitetGroups, enhederGroups, exchangeGroups, appGroups, rollerGroups, caGroups, sikkerhedGroups, sharepointGroups, licenserGroups]
  );

  const nonEmptyGroups = allGroups.filter((g) => g.items.length > 0 && (g.tone === 'bad' || g.tone === 'warn'));

  // ---- I orden: the same checks seen from the other side. What passes, out of everything that was checked. ----
  // Only checks where the whole population is known are here; each group says "N af M i orden". Nothing is guessed.
  const okGroups = useMemo(() => {
    const out = [];
    // Something in order is in order everywhere: an object that is in a red or orange finding never stands here, whatever else
    // passes. The same person has one identity across Brugere and Roller; a role finding is about the role itself.
    const flagged = new Set();
    nonEmptyGroups.forEach((g) => g.items.forEach((i) => flagged.add(g.area === 'roles' ? `role::${i.id}` : `${g.area === 'identity' ? 'person' : g.area}::${i.id}`)));
    const isFlagged = (area, item) => (item.flags || [`${area === 'identity' ? 'person' : area}::${item.id}`]).some((f) => flagged.has(f));
    const group = (key, area, title, hint, all, total) => {
      const items = all.filter((i) => !isFlagged(area, i));
      if (items.length > 0) out.push({ key, tone: 'ok', area, title, hint, why: OK_WHY[key], items, total });
    };
    if (mfa?.users) {
      const WEAK = new Set(['mobilePhone', 'alternateMobilePhone', 'officePhone', 'email']);
      const members = mergeMembers(mfa, risks);
      const people = members.filter((u) => u.accountEnabled !== false && !isMicrosoftSystemAccount(u));
      const withMfa = people.filter((u) => u.hasMfa);
      const strong = withMfa.filter((u) => Array.isArray(u.methods) && u.methods.length > 0 && !u.methods.every((m) => WEAK.has(m)));
      const toItem = (u, sub) => ({ ...personItemOf(u), sub });
      // One group: an account with a strong method (Authenticator or a security key) says so on its own row, it is not a heading of its own.
      const strongIds = new Set(strong.map((u) => u.id));
      group('ok-id-mfa', 'identity', 'MFA er sat op', 'Konti med totrinsgodkendelse', withMfa.map((u) => toItem(u, strongIds.has(u.id) ? 'Har MFA, stærk metode' : 'Har MFA')), people.length);
    }
    if (risks?.privilegedRoles && mfa?.users) {
      const mfaById = new Map(mfa.users.map((u) => [u.id, u.hasMfa]));
      const holders = risks.privilegedRoles.filter((r) => r.status !== 'eligible' && r.status !== 'activated' && r.principalType === 'Bruger' && !r.isGuest);
      const okHolders = holders.filter((r) => mfaById.get(r.principalId) === true);
      group('ok-roller-mfa', 'roles', 'Administratorer med MFA', 'Administratorer med totrinsgodkendelse', okHolders.map((r) => ({ id: `${r.role}:${r.principalId}`, lookupId: r.role, name: r.displayName || 'Ukendt', sub: r.role, meta: [['Rolle', r.role], ['Tildeling', 'Permanent, ikke PIM'], ['Ekstra loginbeskyttelse', 'Aktiveret']], flags: [`role::${r.role}`, `person::${r.principalId}`], navPath: `roller/${encodeURIComponent(r.role)}` })), holders.length);
    }
    if (devices?.devices) {
      const all = devices.devices;
      const failsAPolicy = (d) => !d.statesFromEarlierRead && (d.compliancePolicyStates || []).some((s) => ['nonCompliant', 'error', 'conflict'].includes(s.state));
      const toItem = (d) => ({
        id: d.id, name: d.deviceName || 'Ukendt', sub: d.userDisplayName || '—', navPath: `enheder/${d.id}`, platform: platformOf(d.operatingSystem),
        meta: [deviceOsRow(d), ...deviceReadyRows(d), deviceEncRow(d), deviceSyncRow(d)],
      });
      group('ok-enheder', 'devices', 'Enheder i orden', 'Lever op til jeres regler', all.filter((d) => d.complianceState === 'compliant' && !failsAPolicy(d)).map(toItem), all.length);
      group('ok-enheder-krypteret', 'devices', 'Krypterede enheder', 'Disken er krypteret', all.filter((d) => d.isEncrypted === true).map(toItem), all.length);
    }
    // E-mail: the mailboxes Exchange reports on are the whole population; domains are the domains Exchange lists.
    if (membership?.users && insights) {
      const forwarded = new Set((insights.forwardingRules || []).filter((x) => x.isExternal).map((x) => (x.mailbox || '').toLowerCase()));
      const userByUpn = new Map(membership.users.map((u) => [(u.userPrincipalName || '').toLowerCase(), u]));
      const boxes = (insights.mailboxUsage || []).map((m) => ({ ...m, user: userByUpn.get((m.userPrincipalName || '').toLowerCase()) })).filter((m) => m.user);
      const mailItem = (m) => ({ id: m.user.id, name: m.user.displayName || 'Ukendt', sub: m.user.mail || m.user.userPrincipalName, navPath: `identitet/${m.user.id}`, meta: m.quotaBytes > 0 ? mailboxUsageRows(m) : [] });
      group('ok-exchange-forward', 'exchange', 'Postkasser uden ekstern videresendelse', 'Mails sendes ikke videre uden for organisationen', boxes.filter((m) => !forwarded.has((m.userPrincipalName || '').toLowerCase())).map(mailItem), boxes.length);
      const domains = insights.emailAuthDomains || (insights.emailAuth ? [insights.emailAuth] : []);
      group('ok-exchange-domain', 'exchange', 'Domæner med SPF og DMARC', 'SPF og DMARC er sat op, og DMARC håndhæver', domains.filter((d) => d.spf?.configured && d.dmarc?.configured && d.dmarc.policy !== 'none').map((d) => ({ id: `domain:${d.domain}`, name: d.domain, sub: `DMARC: ${d.dmarc.policy || 'ukendt'}`, navPath: 'exchange', domainAuth: d, meta: domainMeta(d) })), domains.length);
    }
    // Loginregler: the rules that are switched on, out of all of them.
    if (ca?.policies && ca.policies.length > 0) {
      group('ok-ca-enabled', 'conditional-access', 'Aktive loginregler', 'Reglen er slået til og håndhæver', ca.policies.filter((p) => p.state === 'enabled').map((p) => ({ id: p.id, name: p.displayName || 'Loginregel', sub: 'Aktiv', navPath: 'conditional-access' })), ca.policies.length);
    }
    return out;
  }, [mfa, risks, devices, membership, insights, ca, nonEmptyGroups]);

  return { allGroups, nonEmptyGroups, okGroups };
}
